import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import React, { useEffect } from "react";
import Seo from "@/components/Seo";
import { trackPageView, trackEvent } from "@/lib/analytics";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import WebDesign from "@/pages/services/WebDesign";
import WebDevelopment from "@/pages/services/WebDevelopment";
import Ecommerce from "@/pages/services/Ecommerce";
import MobileApp from "@/pages/services/MobileApp";
import SEO from "@/pages/services/SEO";
import SoftwareDevelopment from "@/pages/services/SoftwareDevelopment";
import Portfolio from "@/pages/Portfolio";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import Team from "@/pages/Team";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import IndiaEmergingHub from "@/pages/IndiaEmergingHub";
import PrivacyPage from "@/components/Privacy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/not-found";
import TermsOfService from "./components/TermsOfService";
import CRMPage from "./pages/products/crm";
import DialerPage from "./pages/products/dialer";
import HostingPage from "./pages/products/hosting";
import SSLCertificatePage from "./pages/products/sslcertificate";
import TaskManagementPage from "./pages/products/taskmanagement";
import IVRPage from "./pages/products/ivr";
import ERPPage from "./pages/products/erp";
import DigisocietyPage from "./pages/products/digisociety";
import WhatIsCrmPage from "./pages/products/crm/WhatIsCRM";
import CRMSoftwarePage from "./pages/products/crm/CrmSoftware";
import FeaturePage from "./pages/products/crm/features";
import JustdialIntegrationPage from "./pages/products/crm/JustdialIntegration";
import SulekhaPage from "./pages/products/crm/Sulekha";
import MaintenanceandSupportPage from "./pages/services/MaintenanceandSupport";
import CloudCallCenterPage from "./pages/services/CloudCallCenter";
import IvrSystemPage from "./pages/services/IvrSystem";
import MicroGccIndiaPage from "./pages/services/MicroGccIndia";
import WhatsappBusinessApiPage from "./pages/services/WhatsappBusinessApi";
import {
  AIChatbotDevelopmentPage,
  WhatsappAIResponsePage,
  AIAgentAutomationPage,
  GenerativeAIIntegrationPage,
  AICrmLeadScoringPage,
  AIDocumentProcessingPage,
  AIDataAnalyticsPage,
} from "./pages/services/AiServiceDetail";

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/team" component={Team} />
      <Route path="/career" component={Careers} />
      <Route path="/blog" component={Blog} />
      <Route path="/blogs/:slug" component={BlogPost} />
      <Route path="/india-emerging-hub" component={IndiaEmergingHub} />
      <Route path="/service" component={Services} />
      <Route path="/webdesign" component={WebDesign} />
      <Route path="/software-development-in-jaipur" component={WebDevelopment} />
      <Route path="/ecommerce-development-services" component={Ecommerce} />
      <Route path="/top-software-companies-in-jaipur" component={MobileApp} />
      <Route path="/it-companies-in-jaipur" component={SEO} />
      <Route path="/services/software-development" component={SoftwareDevelopment} />
      <Route path="/services/micro-gcc-india" component={MicroGccIndiaPage} />
      <Route path="/services/whatsapp-business-api" component={WhatsappBusinessApiPage} />
      <Route path="/services/ai-chatbot-development" component={AIChatbotDevelopmentPage} />
      <Route path="/services/whatsapp-ai-response" component={WhatsappAIResponsePage} />
      <Route path="/services/ai-agent-automation" component={AIAgentAutomationPage} />
      <Route path="/services/generative-ai-integration" component={GenerativeAIIntegrationPage} />
      <Route path="/services/ai-crm-lead-scoring" component={AICrmLeadScoringPage} />
      <Route path="/services/ai-document-processing" component={AIDocumentProcessingPage} />
      <Route path="/services/ai-data-analytics" component={AIDataAnalyticsPage} />
      <Route path="/portfolio" component={Portfolio} />
      <Route path="/whoweare" component={About} />
      <Route path="/privacy-policy" component={PrivacyPage} />
      <Route path="/terms-of-service" component={TermsOfService} />
      <Route path="/getintouch" component={Contact} />
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/admin/dashboard" component={AdminDashboard} />
      <Route path="/crm" component={CRMPage}/>
      <Route path="/products/dialer" component={DialerPage}/>
      <Route path="/products/hosting" component={HostingPage}/>
      <Route path="/products/sslcertificate" component={SSLCertificatePage}/>
      <Route path="/products/taskmanagement" component={TaskManagementPage}/>
      <Route path="/products/ivr" component={IVRPage}/>
      <Route path="/products/erp" component={ERPPage}/>
      <Route path="/products/digisociety" component={DigisocietyPage}/>
      <Route path="/crm/what-is-crm" component={WhatIsCrmPage}/>
      <Route path="/crm/crm-software" component={CRMSoftwarePage}/>
      <Route path="/crm/features" component={FeaturePage}/>
      <Route path="/crm/justdial-integration" component={JustdialIntegrationPage}/>
      <Route path="/crm/sulekha" component={SulekhaPage}/>
      <Route path="/maintenanceandsupport" component={MaintenanceandSupportPage}/>
      <Route path="/cloud-call-center" component={CloudCallCenterPage}/>
      <Route path="/ivr-system" component={IvrSystemPage}/>
      <Route component={NotFound} />
    </Switch>
  );
}

function PageViewTracker() {
  const [location] = useLocation();

  useEffect(() => {
    // Defer slightly so the Seo component's document.title update is applied first.
    const id = window.setTimeout(() => trackPageView(location), 0);
    return () => window.clearTimeout(id);
  }, [location]);

  return null;
}

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    // Don't override deep links that target an in-page section (e.g. /page#contact).
    if (window.location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);

  return null;
}

function ContactClickTracker() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const a = target?.closest("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        trackEvent("contact_click", { method: "phone", value: href.replace("tel:", "") });
      } else if (href.startsWith("mailto:")) {
        trackEvent("contact_click", { method: "email", value: href.replace("mailto:", "") });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}

function ImageSeoDefaults() {
  const [location] = useLocation();

  useEffect(() => {
    const images = Array.from(document.querySelectorAll<HTMLImageElement>("img"));
    images.forEach((image, index) => {
      if (!image.hasAttribute("decoding")) image.setAttribute("decoding", "async");
      if (!image.hasAttribute("loading") && index > 2) image.setAttribute("loading", "lazy");
      if (!image.hasAttribute("alt")) image.setAttribute("alt", "");
    });
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    const reveals = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (!reveals.length) return;

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      reveals.forEach((r) => r.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          if (entry.isIntersecting) el.classList.add("is-visible");
          else el.classList.remove("is-visible");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    reveals.forEach((el) => observer.observe(el));

    const overlay = document.getElementById("page-transition-overlay");

    const replayHash = (idFrom?: string) => {
      const id = idFrom ?? location.hash?.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        el.classList.remove("is-visible");
        void (el as HTMLElement).offsetWidth;
        el.classList.add("is-visible");
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const playTransition = (cb?: () => void) => {
      if (!overlay) {
        cb?.();
        return;
      }
      overlay.classList.remove("hidden");
      void overlay.offsetWidth;
      setTimeout(() => {
        overlay.classList.add("hidden");
        if (cb) setTimeout(cb, 180);
      }, 140);
    };

    const onAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const a = target.closest("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("#")) {
        e.preventDefault();
        const id = href.slice(1);
        playTransition(() => replayHash(id));
      }
    };

    const onHashChange = () => {
      playTransition(() => replayHash());
    };

    window.addEventListener("hashchange", onHashChange);
    document.addEventListener("click", onAnchorClick, true);

    if (window.location.hash) {
      history.pushState("", document.title, window.location.pathname + window.location.search);
    }

    setTimeout(() => {
      playTransition(() => replayHash());
    }, 50);

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("click", onAnchorClick, true);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div id="page-transition-overlay" className="hidden">
        <div className="bar" />
      </div>
      <TooltipProvider>
        <Seo />
        <ScrollToTop />
        <PageViewTracker />
        <ContactClickTracker />
        <ImageSeoDefaults />
        <Toaster />
        <Router />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
